import { useState, useMemo, useCallback, useEffect } from 'react';
import { MapPin, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useData } from '../../contexts/DataContext';
import { isActiveWO } from '../../utils/dataQueries';
import { colors, Header, Card } from '../shared';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Milwaukee center coordinates
const MILWAUKEE_CENTER = { lat: 43.0389, lng: -87.9065 };

// Clean minimal dark map styles
const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }, { visibility: 'off' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
];

const mapContainerStyle = {
  width: '100%',
  height: '280px',
};

const TeamMapScreen = () => {
  const { technicians, laborEntries, workOrders, properties, getCurrentTech } = useData();

  const [visibleTechs, setVisibleTechs] = useState([]);
  const [initialized, setInitialized] = useState(false);

  const currentTech = getCurrentTech();

  // Initialize with only the current user visible
  useEffect(() => {
    if (currentTech && !initialized) {
      setVisibleTechs([currentTech.name]);
      setInitialized(true);
    }
  }, [currentTech, initialized]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Calculate tech status and locations from labor entries
  const techData = useMemo(() => {
    return technicians.map((tech, index) => {
      // Find active labor entry (no stop time)
      const activeEntry = laborEntries.find(entry =>
        entry.techName?.toLowerCase() === tech.name?.toLowerCase() &&
        entry.startTime &&
        !entry.stopTime
      );

      // Get today's completed WOs
      const today = new Date().toDateString();
      const todayEntries = laborEntries.filter(entry => {
        const entryDate = new Date(entry.startTime).toDateString();
        return entry.techName?.toLowerCase() === tech.name?.toLowerCase() &&
               entryDate === today &&
               entry.stopTime;
      });

      const completedWOs = todayEntries.map(entry =>
        workOrders.find(wo => wo.id === entry.woId)
      ).filter(Boolean);

      // Get upcoming WOs (open, assigned to tech)
      const upcomingWOs = workOrders.filter(wo =>
        wo.tech?.toLowerCase() === tech.name?.toLowerCase() &&
        isActiveWO(wo) &&
        wo.id !== activeEntry?.woId
      );

      // Determine status
      let status = 'scheduled';
      if (activeEntry) {
        const currentWO = workOrders.find(wo => wo.id === activeEntry.woId);
        status = currentWO?.priority === 'urgent' ? 'urgent' : 'active';
      } else if (completedWOs.length > 0 && upcomingWOs.length === 0) {
        status = 'done';
      }

      // Get current WO
      const currentWO = activeEntry ? workOrders.find(wo => wo.id === activeEntry.woId) : null;

      // Get property location for current WO
      const currentProperty = currentWO ? properties.find(p => p.id === currentWO.propertyId) : null;

      // Assign colors based on index
      const techColors = ['#4ADE80', '#60A5FA', '#F472B6', '#FBBF24', '#A78BFA', '#F87171'];

      // Helper to get coordinates for a WO
      const getWOCoords = (wo) => {
        const prop = properties.find(p => p.id === wo.propertyId);
        if (prop?.lat && prop?.lng) {
          return { lat: prop.lat, lng: prop.lng };
        }
        // Fallback: random position near Milwaukee for demo
        return {
          lat: MILWAUKEE_CENTER.lat + (Math.random() - 0.5) * 0.1,
          lng: MILWAUKEE_CENTER.lng + (Math.random() - 0.5) * 0.1,
        };
      };

      return {
        ...tech,
        color: techColors[index % techColors.length],
        status,
        currentWO: currentWO ? {
          ...currentWO,
          coords: currentProperty?.lat ? { lat: currentProperty.lat, lng: currentProperty.lng } : getWOCoords(currentWO),
        } : null,
        completed: completedWOs.map(wo => ({
          ...wo,
          coords: getWOCoords(wo),
        })),
        upcoming: upcomingWOs.slice(0, 3).map(wo => ({
          ...wo,
          coords: getWOCoords(wo),
        })),
      };
    });
  }, [technicians, laborEntries, workOrders, properties]);

  const toggleTech = (name) => {
    setVisibleTechs(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const getStatusIcon = (status) => {
    if (status === 'urgent') return <AlertTriangle size={16} style={{ color: colors.red }} />;
    if (status === 'active') return <MapPin size={16} style={{ color: colors.green }} />;
    if (status === 'done') return <CheckCircle size={16} style={{ color: colors.textMuted }} />;
    return <Clock size={16} style={{ color: colors.yellow }} />;
  };

  // Create SVG marker icon
  const createMarkerIcon = (color, isActive = false, label = '') => {
    const size = isActive ? 32 : 24;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="${color}" ${isActive ? 'stroke="#fff" stroke-width="2"' : 'opacity="0.8"'}/>
        ${label ? `<text x="12" y="16" text-anchor="middle" fill="#000" font-size="10" font-weight="bold">${label}</text>` : ''}
      </svg>
    `;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: isLoaded ? new window.google.maps.Size(size, size) : null,
      anchor: isLoaded ? new window.google.maps.Point(size / 2, size / 2) : null,
    };
  };

  // Render loading or error states
  if (loadError) {
    return (
      <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
        <Header title="Team Map" subtitle="Milwaukee Metro Area" />
        <div className="px-4">
          <Card className="p-4 text-center">
            <p style={{ color: colors.red }}>Error loading Google Maps</p>
            <p className="text-sm mt-2" style={{ color: colors.textMuted }}>
              Please check your API key configuration
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pb-20" style={{ background: colors.black }}>
      <Header title="Team Map" subtitle="Milwaukee Metro Area" />
      <div className="px-4">
        <Card className="mb-4 p-0 overflow-hidden">
          {/* Map Container */}
          <div className="relative" style={{ height: 280 }}>
            {!isLoaded ? (
              <div className="flex items-center justify-center h-full" style={{ background: colors.cardBg }}>
                <p style={{ color: colors.textMuted }}>Loading map...</p>
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={MILWAUKEE_CENTER}
                zoom={11}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                  gestureHandling: 'greedy',
                }}
              >
                {/* Render markers for each visible tech */}
                {techData.filter(t => visibleTechs.includes(t.name)).map(tech => (
                  <div key={tech.name}>
                    {/* Completed WO markers - dimmer */}
                    {tech.completed.map((wo, i) => (
                      <Marker
                        key={`${tech.name}-c-${i}`}
                        position={wo.coords}
                        icon={createMarkerIcon(tech.color, false)}
                        opacity={0.4}
                        onClick={() => setSelectedMarker({ wo, tech, type: 'completed' })}
                      />
                    ))}

                    {/* Upcoming WO markers */}
                    {tech.upcoming.map((wo, i) => (
                      <Marker
                        key={`${tech.name}-u-${i}`}
                        position={wo.coords}
                        icon={createMarkerIcon(tech.color, false)}
                        opacity={0.8}
                        onClick={() => setSelectedMarker({ wo, tech, type: 'upcoming' })}
                      />
                    ))}

                    {/* Current WO marker - larger with label */}
                    {tech.currentWO && (
                      <Marker
                        key={`${tech.name}-current`}
                        position={tech.currentWO.coords}
                        icon={createMarkerIcon(tech.color, true, tech.name[0])}
                        onClick={() => setSelectedMarker({ wo: tech.currentWO, tech, type: 'current' })}
                      />
                    )}
                  </div>
                ))}

                {/* Info Window for selected marker */}
                {selectedMarker && (
                  <InfoWindow
                    position={selectedMarker.wo.coords}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div style={{ color: '#000', maxWidth: 200 }}>
                      <p className="font-bold">{selectedMarker.tech.name}</p>
                      <p className="text-sm font-medium">{selectedMarker.wo.id}</p>
                      <p className="text-xs">{selectedMarker.wo.property}</p>
                      <p className="text-xs text-gray-600">{selectedMarker.wo.description}</p>
                      <p className="text-xs mt-1 capitalize" style={{
                        color: selectedMarker.type === 'completed' ? '#666' :
                               selectedMarker.type === 'current' ? '#16a34a' : '#ca8a04'
                      }}>
                        {selectedMarker.type === 'completed' ? 'Completed' :
                         selectedMarker.type === 'current' ? 'In Progress' : 'Upcoming'}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>

          {/* Legend */}
          <div className="p-3 flex gap-4 justify-center border-t" style={{ borderColor: colors.border }}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: colors.green }} />
              <span className="text-xs" style={{ color: colors.textSecondary }}>Active</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: colors.yellow }} />
              <span className="text-xs" style={{ color: colors.textSecondary }}>Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full opacity-40" style={{ background: colors.textSecondary }} />
              <span className="text-xs" style={{ color: colors.textSecondary }}>Done</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: colors.red }} />
              <span className="text-xs" style={{ color: colors.textSecondary }}>Urgent</span>
            </div>
          </div>
        </Card>

        <p className="font-semibold mb-3" style={{ color: colors.textSecondary }}>Toggle Team Members</p>
        <div className="space-y-2">
          {techData.map((t) => (
            <button
              key={t.name}
              onClick={() => toggleTech(t.name)}
              className="w-full rounded-2xl p-3 flex items-center gap-3 transition-all"
              style={{
                background: colors.cardBg,
                opacity: visibleTechs.includes(t.name) ? 1 : 0.4,
                border: visibleTechs.includes(t.name) ? `2px solid ${t.color}` : `2px solid transparent`
              }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                style={{ background: t.color, color: colors.black }}
              >
                {t.name[0]}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold" style={{ color: colors.textPrimary }}>{t.name}</p>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {t.currentWO ? t.currentWO.property :
                   t.upcoming.length > 0 ? `${t.upcoming.length} scheduled` :
                   `${t.completed.length} completed`}
                </p>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  {t.currentWO?.description || (t.status === 'done' ? 'Day complete' : 'Starting soon')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs px-2 py-1 rounded" style={{ background: colors.inputBg, color: colors.textMuted }}>
                  {t.completed.length}✓ {t.upcoming.length}→
                </div>
                {getStatusIcon(t.status)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamMapScreen;
